/**
 * La cuadrícula: hasta 4 paneles abiertos a la vez (pedido de Danny, 30-sep-2026). Un panel es un chat de chaggu,
 * un correo de tu buzón o una conversación de WhatsApp (claves en grid-keys.ts). Se llevan ahí arrastrando una fila,
 * con el botón Fijar o desde la bandeja que sube al arrastrar cuando la cuadrícula no se ve (Tray.tsx).
 * - En /c/:id el chat del URL es el activo; en /cuadricula el activo es el último que tocaste.
 * - Un panel fijado nunca se reemplaza solo: con la cuadrícula llena cede otro.
 * - Todo queda guardado en este navegador para volver a encontrarlo igual.
 * - En pantallas angostas solo se ve el activo.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { navigate } from './router.ts';
import { CLASSIC_PANES, MAX_PANES_DEFAULT, TASKS_KEY, isChatKey, placeIntoGrid, replaceIndex, splitMain } from './grid-keys.ts';
import { gridSpanLayout, layoutAfterPlacement, sanitizePanePositions, type PanePosition } from './grid-span-layout.ts';

export const MAX_PANES = MAX_PANES_DEFAULT;
/** Desde qué ancho de ventana hay paneles (la app de Mac abre en ~1000 px: con 1100 no aparecían). */
export const SPLIT_MEDIA = '(min-width: 860px)';
export const splitAvailable = () => typeof matchMedia !== 'undefined' && matchMedia(SPLIT_MEDIA).matches;
/** ¿La ventana es lo bastante ancha para paneles? (en pantallas angostas no hay cuadrícula: solo el activo). */
export function useWide() {
  const [wide, setWide] = useState(() => typeof matchMedia === 'undefined' || matchMedia(SPLIT_MEDIA).matches);
  useEffect(() => {
    const m = matchMedia(SPLIT_MEDIA);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}
/** Tipos del arrastre (dataTransfer): una conversación de chaggu, un correo, una conversación de WhatsApp y un mensaje de WhatsApp. */
export const DRAG_TYPE = 'application/x-chaggu-conversation';
export const DRAG_MAIL = 'application/x-chaggu-mail';
export const DRAG_WA = 'application/x-chaggu-wa';
export const DRAG_WAMSG = 'application/x-chaggu-wamsg';
/** Una sección entera (Tareas, Correo, WhatsApp) arrastrada desde el riel o desde su página. */
export const DRAG_SECTION = 'application/x-chaggu-section';
/** Una tarea suelta: se lleva a un chat como enlace. */
export const DRAG_TASK = 'application/x-chaggu-task';
export type DragKind = 'chat' | 'mail' | 'wa' | 'wamsg' | 'section' | 'task';
const DRAG_KINDS: [string, DragKind][] = [[DRAG_TYPE, 'chat'], [DRAG_MAIL, 'mail'], [DRAG_WA, 'wa'], [DRAG_WAMSG, 'wamsg'], [DRAG_SECTION, 'section'], [DRAG_TASK, 'task']];
/** Qué se está arrastrando (por los tipos del dataTransfer, que sí se ven mientras se arrastra). */
export function dragKindOf(types: readonly string[] | DOMStringList): DragKind | null {
  const list = Array.from(types as ArrayLike<string>);
  return DRAG_KINDS.find(([type]) => list.includes(type))?.[1] ?? null;
}
export const dragType = (k: DragKind) => DRAG_KINDS.find(([, kind]) => kind === k)![0];
/** Lo que se puede soltar en un cuadrito (todo menos un mensaje suelto) y lo que se puede llevar a un chat (correo y mensaje). */
export const fitsSlot = (k: DragKind | null) => k === 'chat' || k === 'mail' || k === 'wa' || k === 'section';
export const fitsChat = (k: DragKind | null) => k === 'mail' || k === 'wamsg' || k === 'task';

const KEY = 'chaggu:split';
const PIN_KEY = 'chaggu:split-pinned';
const META_KEY = 'chaggu:split-meta';
const SIDE_KEY = 'chaggu:grid-side';

function read<T>(key: string, fallback: T): T { try { const v = JSON.parse(localStorage.getItem(key) ?? 'null'); return v ?? fallback; } catch { return fallback; } }
function write(key: string, value: unknown) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* sin almacenamiento */ } }

/** Hasta 4 cuaditos más la columna de Tareas. */
const MAX_STORED = MAX_PANES + 1;
function load(): string[] { const v = read<unknown>(KEY, []); return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, MAX_STORED) : []; }
let panes: string[] = load();
let pinned: Set<string> = new Set(read<string[]>(PIN_KEY, []).filter((k) => typeof k === 'string'));
export interface PaneMeta { title: string; sub?: string }
let metas: Record<string, PaneMeta> = read<Record<string, PaneMeta>>(META_KEY, {});
let activeKey: string | null = null;
let gridSide = read<boolean>(SIDE_KEY, false) === true;
let pulse = 0;
/** Panel que acaba de abrirse (aviso, burbuja, notificación, lista): brilla 1,6 s para saber cuál es (2-oct-2026). */
let flashKey: string | null = null;
let flashTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

// Expansion changes presentation only. History and reload retain the canonical grid.
const expandedFromUrl = () => new URLSearchParams(location.search).get('pane');
let expandedKey = expandedFromUrl();
window.addEventListener('popstate', () => { expandedKey = expandedFromUrl(); emit(); });
window.addEventListener('chaggu:navigate', () => { expandedKey = expandedFromUrl(); emit(); });
export const useExpandedPane = () => useSyncExternalStore(subscribe, () => expandedKey);
export function collapsePane() {
  const url = new URL(location.href); url.searchParams.delete('pane');
  history.replaceState(history.state, '', url); expandedKey = null; emit();
}
export type GridLayout = 'classic' | 'tall-center' | 'tall-left' | 'tall-right' | 'custom';
const LAYOUT_KEY = 'chaggu:grid-layout-v1';
const savedLayout = read<{ version?: number; kind?: GridLayout; order?: string[]; tall?: string[]; wide?: string[]; positions?: Record<string, PanePosition>; columns?: number[] }>(LAYOUT_KEY, {});
let layout: GridLayout = savedLayout.version === 1 && ['classic', 'tall-center', 'tall-left', 'tall-right', 'custom'].includes(savedLayout.kind ?? '') ? savedLayout.kind! : 'classic';
let layoutOrder: string[] = Array.isArray(savedLayout.order) ? savedLayout.order.filter((x): x is string => typeof x === 'string') : [];
let tallPanes: string[] = Array.isArray(savedLayout.tall) ? savedLayout.tall.filter((x): x is string => typeof x === 'string') : [TASKS_KEY];
let widePanes: string[] = Array.isArray(savedLayout.wide) ? savedLayout.wide.filter((x): x is string => typeof x === 'string') : [];
let panePositions: Record<string, PanePosition> = sanitizePanePositions(savedLayout.positions, panes);
let columnSizes = Array.isArray(savedLayout.columns) ? savedLayout.columns.slice(0, 10).filter((n) => Number.isFinite(n) && n > 0 && n <= 1) : [];
export const useGridColumnSizes = () => useSyncExternalStore(subscribe, () => columnSizes);
export const usePanePositions = () => useSyncExternalStore(subscribe, () => panePositions);
const saveLayout = () => write(LAYOUT_KEY, { version: 1, kind: layout, order: layoutOrder, tall: tallPanes, wide: widePanes, positions: panePositions, columns: columnSizes });
export const useGridLayout = () => useSyncExternalStore(subscribe, () => layout);
export const useLayoutOrder = () => useSyncExternalStore(subscribe, () => layoutOrder);
export const useTallPanes = () => useSyncExternalStore(subscribe, () => tallPanes);
export const useWidePanes = () => useSyncExternalStore(subscribe, () => widePanes);
/**
 * Lo que está en la cuadrícula: los paneles guardados y, además, el chat abierto por URL que no entró en ellos
 * (con los 4 fijados, /c/:id se dibuja encima sin ocupar un lugar). Sin esto, cambiarle el tamaño no hacía nada.
 */
const gridKeys = () => activeKey && !panes.includes(activeKey) ? [...panes, activeKey] : panes;
const onGrid = (k: string) => panes.includes(k) || k === activeKey;
export function setPaneSize(key: string, rows: 1 | 2, columns: 1 | 2, currentTall: readonly string[], currentWide: readonly string[], currentOrder: readonly string[]) {
  const tall = new Set(currentTall), wide = new Set(currentWide);
  if (rows === 2) tall.add(key); else tall.delete(key);
  if (columns === 2) wide.add(key); else wide.delete(key);
  tallPanes = [...tall].filter(onGrid);
  widePanes = [...wide].filter(onGrid);
  layoutOrder = currentOrder.filter(onGrid);
  panePositions = sanitizePanePositions(panePositions, gridKeys());
  if (panePositions[key]) layoutOrder = [key, ...layoutOrder.filter((k) => k !== key)];
  layout = 'custom'; saveLayout(); emit();
}
/** A free-cell drop preserves the visible destination, including an intentionally empty row. */
export function placeGridPane(key: string, position: PanePosition, currentPositions: Readonly<Record<string, PanePosition>>, currentTall: readonly string[], currentWide: readonly string[], currentOrder: readonly string[], widths: readonly number[]) {
  if (!onGrid(key)) return;
  panePositions = sanitizePanePositions({ ...currentPositions, [key]: position }, gridKeys());
  const total = widths.reduce((a, b) => a + b, 0);
  if (total > 0 && widths.length <= 10 && widths.every((w) => Number.isFinite(w) && w > 0)) columnSizes = widths.map((w) => w / total);
  layoutOrder = [key, ...currentOrder.filter((k) => k !== key)].filter(onGrid);
  tallPanes = currentTall.filter(onGrid);
  widePanes = currentWide.filter(onGrid);
  layout = 'custom'; saveLayout(); emit();
}
/** Continuous track sizing and initial preset conversion are one layout transaction. */
export function setGridGeometry(positions: Record<string, PanePosition>, tall: string[], wide: string[], order: string[], widths: number[], row: number) {
  panePositions = sanitizePanePositions(positions, gridKeys());
  tallPanes = tall.filter(onGrid); widePanes = wide.filter(onGrid);
  layoutOrder = order.filter(onGrid);
  const total = widths.reduce((a, b) => a + b, 0);
  if (total > 0 && widths.length <= 10 && widths.every((w) => Number.isFinite(w) && w > 0)) columnSizes = widths.map((w) => w / total);
  // Shared row persistence stays compatible with the existing row slider.
  sizes = { ...sizes, row: Math.max(.2, Math.min(.8, row)) };
  write(SIZE_KEY, sizes);
  sizeListeners.forEach((listener) => listener());
  layout = 'custom'; saveLayout(); emit();
}
export function setPaneRows(key: string, rows: 1 | 2, currentTall: readonly string[]) {
  const next = new Set(layout === 'custom' ? tallPanes : currentTall);
  if (rows === 2) next.add(key); else next.delete(key);
  tallPanes = [...next].filter(onGrid);
  layout = 'custom'; saveLayout(); emit();
}
export function setGridLayout(next: GridLayout) {
  layout = next; if (next !== 'custom') panePositions = {}; saveLayout(); emit();
}
export function moveLayoutPane(key: string, index: number) {
  const order = [...layoutOrder.filter((k) => panes.includes(k)), ...panes.filter((k) => !layoutOrder.includes(k))];
  const at = order.indexOf(key);
  if (at < 0 || index < 0 || index >= order.length || at === index) return;
  [order[at], order[index]] = [order[index]!, order[at]!];
  layoutOrder = order; panePositions = {}; saveLayout(); emit();
}
/** A newly opened/focused pane must not remain in the saved fifth slot of a four-pane preset. */
function revealLayoutPane(key: string) {
  if (layout === 'classic' || layout === 'custom') return;
  const order = [...layoutOrder.filter((k) => panes.includes(k)), ...panes.filter((k) => !layoutOrder.includes(k))];
  const at = order.indexOf(key);
  if (at < 4) return;
  [order[3], order[at]] = [order[at]!, order[3]!];
  layoutOrder = order; saveLayout();
}

/** Closing/replacing panels releases whole empty columns, without erasing deliberate empty row cells. */
function compactStoredPositions() {
  panePositions = sanitizePanePositions(panePositions, panes);
  if (layout !== 'custom') return;
  const order = [...layoutOrder.filter((key) => panes.includes(key)), ...panes.filter((key) => !layoutOrder.includes(key))];
  const packed = gridSpanLayout(order, new Set(tallPanes), new Set(widePanes), panePositions);
  panePositions = Object.fromEntries(Object.entries(packed.cells).map(([key, cell]) => [key, { column: cell.column, row: cell.row }]));
  if (columnSizes.length !== packed.columns) columnSizes = [];
}

function set(next: string[]) {
  const prev = panes;
  panes = next.slice(0, MAX_STORED);
  sizeNewPanes(prev);
  compactStoredPositions();
  saveLayout();
  // Lo que ya no está en la cuadrícula deja de estar fijado y de guardar su nombre.
  pinned = new Set([...pinned].filter((k) => panes.includes(k)));
  metas = Object.fromEntries(Object.entries(metas).filter(([k]) => panes.includes(k)));
  if (activeKey && !panes.includes(activeKey)) activeKey = null;
  write(KEY, panes); write(PIN_KEY, [...pinned]); write(META_KEY, metas);
  emit();
}
/**
 * Tamaño de los paneles que entran (5-oct-2026: «que ocupe todo por defecto, 2 filas»): el que reemplaza a otro hereda su
 * lugar y tamaño; uno nuevo entra a lo alto. Con más de 4 la cuadrícula pasa a «a tu medida», cada uno en su columna, y se
 * desplaza a lo ancho.
 */
function sizeNewPanes(prev: readonly string[]) {
  const added = panes.filter((k) => !prev.includes(k)), removed = prev.filter((k) => !panes.includes(k));
  if (!added.length) return;
  const inherit = (from: string, to: string) => {
    if (tallPanes.includes(from)) tallPanes = [...tallPanes, to];
    if (widePanes.includes(from)) widePanes = [...widePanes, to];
    if (panePositions[from]) panePositions = { ...panePositions, [to]: panePositions[from]! };
    layoutOrder = layoutOrder.map((k) => k === from ? to : k);
  };
  if (layout !== 'custom' && splitMain(panes).main.length > CLASSIC_PANES) {
    layout = 'custom'; panePositions = {};
    tallPanes = [...new Set([...splitMain(panes).main, TASKS_KEY])];
    return;
  }
  if (layout !== 'custom') return;
  added.forEach((key, i) => { const from = removed[i]; if (from) inherit(from, key); else if (!tallPanes.includes(key)) tallPanes = [...tallPanes, key]; });
}
export const usePanes = () => useSyncExternalStore(subscribe, () => panes);
export const currentPanes = () => panes;
export const usePinned = () => useSyncExternalStore(subscribe, () => pinned);
export const isPinned = (key: string) => pinned.has(key);
export const useMetas = () => useSyncExternalStore(subscribe, () => metas);
export const useActiveKey = () => useSyncExternalStore(subscribe, () => activeKey);
export const usePulse = () => useSyncExternalStore(subscribe, () => pulse);
export const useFlash = () => useSyncExternalStore(subscribe, () => flashKey);
/** Hace brillar el panel `key` y deja el cursor en su caja de escribir. */
export function flashPane(key: string, focus = true) {
  flashKey = key; clearTimeout(flashTimer);
  flashTimer = setTimeout(() => { flashKey = null; emit(); }, 1600);
  emit();
  if (focus) requestAnimationFrame(() => requestAnimationFrame(() => {
    const cell = document.querySelector<HTMLElement>(`[data-pane="${CSS.escape(key)}"]`) ?? document;
    cell.querySelector<HTMLTextAreaElement>('.composer textarea')?.focus({ preventScroll: true });
  }));
}
/** Nombre de un panel que no es un chat de chaggu (para la bandeja, el riel y el título mientras carga). */
export function rememberMeta(key: string, meta: PaneMeta) {
  if (metas[key]?.title === meta.title && metas[key]?.sub === meta.sub) return;
  metas = { ...metas, [key]: meta }; write(META_KEY, metas); emit();
}

export function togglePin(key: string): boolean {
  if (!panes.includes(key)) return false;
  const next = new Set(pinned);
  if (next.has(key)) next.delete(key); else next.add(key);
  pinned = next; write(PIN_KEY, [...pinned]); emit();
  return pinned.has(key);
}

/** La conversación del URL (/c/:id), si se está viendo una. */
function routeChatId(): string | null {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  const m = /^\/c\/([^/?#]+)/.exec(location.pathname.slice(base.length));
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * Los paneles a mostrar con `active` (el del URL) adentro: si no estaba, reemplaza al activo anterior
 * (o se agrega si no había paneles). Así tocar un chat en la lista cambia el panel que tienes enfocado.
 */
let lastActive: string | null = null;
export function syncActive(active: string) {
  activeKey = active;
  if (panes.length === 0 || panes.includes(active)) { lastActive = active; revealLayoutPane(active); emit(); return; }
  // Tareas va en su columna y no entra en estas cuentas.
  const { main, tasks } = splitMain(panes);
  const at = lastActive ? main.indexOf(lastActive) : -1;
  const next = [...main];
  // Un panel fijado no cede su lugar al chat que abres: ese entra en otro hueco (o reemplaza al último sin fijar).
  if (at >= 0 && !pinned.has(lastActive!)) next[at] = active;
  else if (next.length < MAX_PANES) next.unshift(active);
  else { const r = replaceIndex(next, pinned); if (r >= 0) next[r] = active; else { lastActive = active; emit(); return; } }
  lastActive = active;
  set([...new Set(next), ...(tasks ? [TASKS_KEY] : [])]);
  revealLayoutPane(active); emit();
}

export type OpenResult = 'opened' | 'focused' | 'blocked';
/**
 * Abre `key` en paralelo junto a `active` (la conversación que se está viendo). Con 4 abiertos, reemplaza a
 * `replace` (el panel donde se soltó) o al último sin fijar. Si ya estaba abierta, la enfoca; si todos los paneles
 * están fijados, no hace nada y devuelve 'blocked'.
 */
export function openBeside(key: string, active: string | null, replace?: string | null): OpenResult {
  const base = panes.length ? [...panes] : active ? [active] : [];
  if (base.includes(key)) { focusPane(key); return 'focused'; }
  // Tareas va a su columna (a la derecha, de arriba a abajo) y no gasta uno de los 4 cuaditos.
  const { main, tasks } = splitMain(base);
  let next: string[];
  if (key === TASKS_KEY) next = [...main, TASKS_KEY];
  else if (main.length < MAX_PANES) next = [...main, key, ...(tasks ? [TASKS_KEY] : [])];
  else {
    const at = replaceIndex(main, pinned, replace ?? null);
    if (at < 0) return 'blocked';
    const m = [...main]; m[at] = key;
    next = [...m, ...(tasks ? [TASKS_KEY] : [])];
  }
  set(next);
  activeKey = key;
  revealLayoutPane(key);
  if (!active && isChatKey(key)) navigate(`/c/${key}`);
  emit();
  return 'opened';
}

/**
 * Fija `key` en el cuadrito `index` (0–3) con su nombre: un hueco libre lo agrega al final, uno ocupado lo reemplaza.
 * Devuelve el lugar (0–3) o -1 si ese cuadrito es de un panel fijado o la cuadrícula está llena.
 */
export function pinAt(key: string, index: number, meta?: PaneMeta): number {
  const before = [...panes];
  const r = placeIntoGrid(panes, key, index, pinned, MAX_PANES);
  if (!r) return -1;
  if (r.replaced) pinned.delete(r.replaced);
  if (r.panes !== panes) set(r.panes);
  // Preserve visual layout order while applying the explicit slot move to the keys occupying it.
  if (layoutOrder.length) {
    layoutOrder = layoutAfterPlacement(layoutOrder, before, panes, key, index);
    saveLayout();
  }
  pinned = new Set(pinned).add(key);
  if (meta) metas = { ...metas, [key]: meta };
  activeKey = key; lastActive = key;
  revealLayoutPane(key);
  write(PIN_KEY, [...pinned]); write(META_KEY, metas);
  pulse++;
  emit();
  return r.at;
}

/** Cierra un panel; si era el activo, el activo pasa al vecino. Con uno solo y sin nada fijado, vuelve a la vista normal. */
export function closePane(key: string, active: string | null) {
  if (expandedKey === key) collapsePane();
  const at = panes.indexOf(key);
  const next = panes.filter((x) => x !== key);
  const keep = next.length > 1 || next.some((k) => !isChatKey(k) || pinned.has(k));
  set(keep ? next : []);
  if (key === (active ?? activeKey)) {
    const to = next[Math.min(Math.max(0, at - 1), next.length - 1)];
    if (to) focusPane(to);
  }
}

/** Revoke specific panels without collapsing any surviving panel, pin or layout choice. */
export function removePanes(keys: readonly string[]) {
  if (!keys.length) return;
  const removed = new Set(keys);
  const before = [...panes], previousActive = activeKey;
  if (expandedKey && removed.has(expandedKey)) collapsePane();
  panePositions = Object.fromEntries(Object.entries(panePositions).filter(([key]) => !removed.has(key)));
  layoutOrder = layoutOrder.filter((key) => !removed.has(key));
  tallPanes = tallPanes.filter((key) => !removed.has(key));
  widePanes = widePanes.filter((key) => !removed.has(key));
  panes = panes.filter((key) => !removed.has(key));
  compactStoredPositions();
  saveLayout();
  pinned = new Set([...pinned].filter((key) => !removed.has(key)));
  metas = Object.fromEntries(Object.entries(metas).filter(([key]) => !removed.has(key)));
  if (activeKey && removed.has(activeKey)) activeKey = null;
  write(KEY, panes); write(PIN_KEY, [...pinned]); write(META_KEY, metas); emit();
  if (lastActive && removed.has(lastActive)) lastActive = null;
  if (previousActive && removed.has(previousActive)) {
    const at = before.indexOf(previousActive);
    const next = panes[Math.min(Math.max(0, at - 1), panes.length - 1)];
    if (next) focusPane(next);
  }
}

/** Include saved references even if their panel is currently absent. */
export const currentPaneReferences = () => [...new Set([...panes, ...pinned, ...Object.keys(metas), ...layoutOrder, ...tallPanes, ...widePanes, ...(expandedKey ? [expandedKey] : [])])];

/** Expande sin borrar paneles, fijados, metadatos o tamaños. */
export function onlyPane(key: string) {
  if (expandedKey === key) { collapsePane(); return; }
  const url = new URL(location.href); url.searchParams.set('pane', key);
  history.pushState(history.state, '', url); expandedKey = key; activeKey = key; emit();
}

/** Enfocar un panel sin recargar. Un chat cambia el URL solo si ya estás en /c/:id; en la cuadrícula solo se marca. */
export function focusPane(key: string) {
  lastActive = key; activeKey = key;
  revealLayoutPane(key);
  if (isChatKey(key) && routeChatId()) navigate(`/c/${key}`, true);
  emit();
}

/** ¿La conversación está a la vista en algún panel? (para no avisar de lo que ya se está leyendo). */
export const isOpenInPanes = (id: string) => panes.includes(id) && (!expandedKey || expandedKey === id)
  && (layout === 'classic' || layout === 'custom' || [...layoutOrder.filter((k) => panes.includes(k)), ...panes.filter((k) => !layoutOrder.includes(k))].slice(0, 4).includes(id));

// ---------- Cuadrícula al lado de WhatsApp y Correo ----------
export const useGridSide = () => useSyncExternalStore(subscribe, () => gridSide);
export function setGridSide(on: boolean) { gridSide = on; write(SIDE_KEY, on); emit(); }

// ---------- Arrastre en curso (para la bandeja) ----------
let dragging: DragKind | null = null;
export const useDragging = () => useSyncExternalStore(subscribe, () => dragging);
export function setDragging(k: DragKind | null) { if (dragging !== k) { dragging = k; emit(); } }

// ---------- De dónde vengo (el «← Volver» de la cuadrícula) ----------
let back: string | null = null;
export const useBack = () => useSyncExternalStore(subscribe, () => back);
export function rememberBack(path: string | null) { back = path; emit(); }

// ---------- Tamaño de los paneles (se arrastran las divisiones) ----------
/** Fracción del ancho para la columna izquierda y del alto para la fila de arriba (0,2–0,8). */
const SIZE_KEY = 'chaggu:split-size';
let sizes: { col: number; row: number } = (() => {
  try { const v = JSON.parse(localStorage.getItem(SIZE_KEY) ?? 'null'); if (v && v.col > 0 && v.row > 0) return v; } catch { /* */ }
  return { col: 0.5, row: 0.5 };
})();
const sizeListeners = new Set<() => void>();
export const useSplitSizes = () => useSyncExternalStore((l) => { sizeListeners.add(l); return () => { sizeListeners.delete(l); }; }, () => sizes);
export function setSplitSize(p: Partial<{ col: number; row: number }>, persist = false) {
  const clamp = (x: number) => Math.min(0.8, Math.max(0.2, x));
  sizes = { col: clamp(p.col ?? sizes.col), row: clamp(p.row ?? sizes.row) };
  sizeListeners.forEach((l) => l());
  if (persist) { try { localStorage.setItem(SIZE_KEY, JSON.stringify(sizes)); } catch { /* */ } }
}

// ---------- Zoom por conversación (A− / A+ o ⌘/Ctrl + rueda) ----------
const ZOOM_KEY = 'chaggu:conv-zoom';
export const ZOOM_MIN = 0.7, ZOOM_MAX = 1.6;
let zooms: Record<string, number> = (() => { try { return JSON.parse(localStorage.getItem(ZOOM_KEY) ?? '{}') ?? {}; } catch { return {}; } })();
const zoomListeners = new Set<() => void>();
export const useConvZoom = (id: string) => useSyncExternalStore((l) => { zoomListeners.add(l); return () => { zoomListeners.delete(l); }; }, () => zooms[id] ?? 1);
/** Zoom actual sin suscribirse (para los manejadores de eventos). */
export const convZoomNow = (id: string) => zooms[id] ?? 1;
export function setConvZoom(id: string, z: number) {
  const v = Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)) * 10) / 10;
  zooms = { ...zooms };
  if (v === 1) delete zooms[id]; else zooms[id] = v;
  zoomListeners.forEach((l) => l());
  try { localStorage.setItem(ZOOM_KEY, JSON.stringify(zooms)); } catch { /* */ }
}
